/* eslint-disable react/no-unescaped-entities */
import React from "react";
import PageHero from "@/components/landing/PageHero";
import PageMeta from "@/components/PageMeta";

const SECTIONS = [
  {
    h: "1. Introduction",
    p: (
      <>
        Shopbook ("Shopbook", "we", "our", "us") provides business management software across web and mobile for retail businesses and their teams. This Privacy Policy explains what information we collect, how we use it, who we share it with, and what rights you have. By using Shopbook, you agree to the practices described here.
      </>
    ),
  },
  {
    h: "2. Information we collect",
    p: (
      <>
        We collect information needed to operate, secure, support, and improve Shopbook:
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li><strong>Account information:</strong> business name, owner name, phone number, email, country and branch details.</li>
          <li><strong>Authentication data:</strong> PIN (stored as a one-way hash), OTP codes and device identifiers.</li>
          <li><strong>Business data you enter:</strong> products, prices, sales, credit sales, customer names and balances, staff information.</li>
          <li><strong>Device and usage data:</strong> app version, crash reports, performance logs and basic device info (OS, model). The public marketing and legal website does not use third-party session recording.</li>
          <li><strong>Location data:</strong> precise foreground location when you choose a location-dependent feature, such as saving a branch location, enforcing staff sign-in near a branch, or recording where a sale occurred. Shopbook does not request background location.</li>
          <li><strong>Camera access:</strong> camera frames are used on your device to scan product barcodes. Shopbook does not record video or audio for this feature.</li>
          <li><strong>Notification data:</strong> push-subscription identifiers, device platform, language, branch selection and time zone when you enable push notifications.</li>
          <li><strong>AI feature data:</strong> the question, product prompt, and the minimum authorized business records needed to generate an assistant answer, report summary, or product image.</li>
          <li><strong>Communications:</strong> support messages you send us by email or through the app.</li>
        </ul>
      </>
    ),
  },
  {
    h: "3. How we use your information",
    p: (
      <>
        We use your information to:
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li>Operate the service's core features across web and mobile, including sales, inventory, customer credit, expenses, staff, branches, and reports.</li>
          <li>Synchronize authorized workspace records across devices and support account recovery.</li>
          <li>Send you notifications (daily summary, weekly insight, monthly report, low-stock alerts).</li>
          <li>Provide multilingual assistant answers using your own shop data.</li>
          <li>Improve product reliability and detect abuse.</li>
          <li>Communicate with you about updates, security and support.</li>
        </ul>
      </>
    ),
  },
  {
    h: "4. Legal basis for processing",
    p: <>We process your data on the basis of (a) the contract between us when you sign up, (b) your consent (where applicable), and (c) our legitimate interests in operating and improving Shopbook safely. Where consent is required by your country's law, we ask for it before processing.</>,
  },
  {
    h: "5. How we share information",
    p: (
      <>
        We do <strong>not</strong> sell your data. We share information only with:
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li><strong>Infrastructure and diagnostics providers</strong> for cloud hosting, databases and error monitoring, including Sentry for crash and performance diagnostics.</li>
          <li><strong>OneSignal</strong> for push-notification delivery when you enable notifications.</li>
          <li><strong>OpenAI</strong> when you intentionally use an AI-powered assistant, summary, product-name, or image feature. We send only the prompt and authorized context needed for that request.</li>
          <li><strong>Payment processors</strong>, including Squad for supported web purchases, when you choose to make a payment.</li>
          <li><strong>Law enforcement</strong> when legally required, and only for the specific data requested.</li>
        </ul>
        We never sell your personal or business data, use third-party advertising SDKs, use session recording on the public website, or track you across other companies' apps and websites. We do not use your business data to train third-party AI models.
      </>
    ),
  },
  {
    h: "6. Data security",
    p: <>Shopbook uses PIN login, OTP verification, encrypted sessions, role-based permissions and encryption in transit (TLS 1.2+). On-device data is stored in a sandboxed local database. We restrict internal access to your data to the minimum staff required to operate the service.</>,
  },
  {
    h: "7. Data retention",
    p: <>We keep account and business data while the account is active and as needed to provide the service. If you delete the account, access is revoked and the profile enters our deletion and anonymisation process. We retain only records that remain necessary for security, fraud prevention, financial reporting, audit, dispute handling, or another applicable legal obligation, and only for the required period.</>,
  },
  {
    h: "8. Your rights",
    p: (
      <>
        Depending on your country, you may have the right to access, correct, export, restrict or delete your personal data, and to object to certain processing. You can exercise available account controls from Settings on web or mobile, or contact <a href="mailto:privacy@shopbook.tech" className="text-[#0E6B6B] underline">privacy@shopbook.tech</a>. We respond within the period required by applicable law.
      </>
    ),
  },
  {
    h: "9. Children's privacy",
    p: <>Shopbook is intended for business owners aged 18 and above. We do not knowingly collect data from children. If you believe a child has used Shopbook, contact us and we will delete the associated data.</>,
  },
  {
    h: "10. International transfers",
    p: <>Your data may be processed in countries different from where you live. When we transfer personal data internationally, we use appropriate safeguards such as standard contractual clauses.</>,
  },
  {
    h: "11. Changes to this policy",
    p: <>We may update this policy from time to time. We'll notify you in-app and by email when changes are material. The "Last updated" date at the top reflects the latest revision.</>,
  },
  {
    h: "12. Contact us",
    p: (
      <>
        Questions about privacy? Reach our Data Protection contact at <a href="mailto:privacy@shopbook.tech" className="text-[#0E6B6B] underline">privacy@shopbook.tech</a> or write to Shopbook, Yaba, Lagos, Nigeria.
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <PageMeta title="Privacy Policy" description="How Shopbook collects, uses, secures, shares, retains, and deletes personal and business data across web and mobile." />
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy."
        italicTail=""
        intro="Last updated: August 2026. This policy explains how Shopbook handles personal and business data across the web and mobile service."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-0 space-y-10">
          {SECTIONS.map((s, i) => (
            <div key={s.h} data-testid={`privacy-section-${i}`}>
              <h2 className="font-display text-xl md:text-2xl font-semibold text-[#0A2424]">{s.h}</h2>
              <div className="mt-3 text-base text-[#3a4a4a] leading-[1.75]">{s.p}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
